// We don't need to redeclare HeyGoodGame as it's already declared in vanilla/index.ts
// Just declare the additional window properties
declare global {
  interface Window {
    eh2: string;
    ehttd: string;
    google?: {
      accounts: {
        id: {
          initialize: (config: GoogleOneTapConfig) => void;
          prompt: (
            callback?: (notification: GooglePromptNotification) => void,
          ) => void;
        };
      };
    };
  }
}

interface GoogleOneTapConfig {
  client_id: string;
  callback: (response: GoogleCredentialResponse) => void;
  auto_select?: boolean;
  cancel_on_tap_outside?: boolean;
}

interface GoogleCredentialResponse {
  credential: string;
  select_by: string;
}

interface GooglePromptNotification {
  isNotDisplayed: () => boolean;
  isSkippedMoment: () => boolean;
  isDismissedMoment: () => boolean;
  getNotDisplayedReason: () => string;
  getSkippedReason: () => string;
  getDismissedReason: () => string;
}

const AUTH_CHECK_STORAGE_KEY = 'heygg_last_auth_check';
const AUTH_CHECK_INTERVAL = 24 * 60 * 60 * 1000; // 24 hours
const DEFAULT_API_BASE_URL = 'https://play.hey.gg';

function getQueryParam(param: string) {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get(param);
}

function setLocalStorage(key: string, value: number | string | null) {
  if (value === null) {
    localStorage.removeItem(key);
  } else {
    localStorage.setItem(
      key,
      typeof value !== 'string' ? JSON.stringify(value) : value,
    );
  }
}

function getLocalStorage(key: string) {
  const value = localStorage.getItem(key);
  return value ? (typeof value === 'string' ? value : JSON.parse(value)) : null;
}

function redirectToCheckAuth(apiBaseUrl: string, anonymousId?: string | null) {
  const redirectParams = new URLSearchParams();
  redirectParams.set('redirect_uri', window.location.href);
  if (anonymousId) {
    redirectParams.set('anonymous_id', anonymousId);
  }
  window.location.href = `${apiBaseUrl}/check-auth?${redirectParams.toString()}`;
}

async function checkAuth() {
  console.log('[HEYGG COMMON] Checking auth...');
  const API_BASE_URL =
    window.HeyGoodGame?.config?.apiBaseUrl || DEFAULT_API_BASE_URL;
  const now = Date.now();
  const initialToken = getQueryParam('token');
  const initialAnonymousId = getQueryParam('anonymous_id');

  if (initialToken || initialAnonymousId) {
    const cleanSearchParams = new URLSearchParams(window.location.search);
    cleanSearchParams.delete('token');
    cleanSearchParams.delete('anonymous_id');
    const newUrl = `${window.location.pathname}${
      cleanSearchParams.toString() ? `?${cleanSearchParams.toString()}` : ''
    }${window.location.hash}`;
    window.history.replaceState({}, '', newUrl);
  }

  if (initialToken) {
    setLocalStorage('heygg_jwt', initialToken);
    try {
      const response = await fetch(`${API_BASE_URL}/api/user`, {
        headers: {
          Authorization: `Bearer ${initialToken}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) throw new Error('Failed to fetch user details');
      const userData = await response.json();
      window.eh2 = userData.email_eh2;
      window.ehttd = userData.email_ehttd;
      setLocalStorage('heygg_user', userData);
      setLocalStorage('heygg_anonymous_id', null);
    } catch (err: any) {
      console.error(err.message || 'Failed to fetch user data');
    }
    setLocalStorage(AUTH_CHECK_STORAGE_KEY, now);
    return;
  }

  if (initialAnonymousId) {
    setLocalStorage('heygg_anonymous_id', initialAnonymousId);
    setLocalStorage('heygg_jwt', null);
    setLocalStorage('heygg_user', null);
    window.eh2 = '';
    window.ehttd = '';
    setLocalStorage(AUTH_CHECK_STORAGE_KEY, now);
    return;
  }

  const existingJwt = getLocalStorage('heygg_jwt');
  const existingAnonymousId = getLocalStorage('heygg_anonymous_id');

  if (!existingJwt && !existingAnonymousId) {
    redirectToCheckAuth(API_BASE_URL, existingAnonymousId);
    return;
  }

  if (existingJwt) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/user`, {
        headers: {
          Authorization: `Bearer ${existingJwt}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const userData = await response.json();
        window.eh2 = userData.email_eh2;
        window.ehttd = userData.email_ehttd;
        setLocalStorage('heygg_user', userData);
        setLocalStorage('heygg_anonymous_id', null);
        setLocalStorage(AUTH_CHECK_STORAGE_KEY, now);
        return;
      }
      // JWT is invalid, clear it but keep anonymous ID if it exists
      setLocalStorage('heygg_jwt', null);
      setLocalStorage('heygg_user', null);
      window.eh2 = '';
      window.ehttd = '';
    } catch (err) {
      console.warn('Failed to validate existing token:', err);
      // On error, clear JWT but keep anonymous ID
      setLocalStorage('heygg_jwt', null);
      setLocalStorage('heygg_user', null);
      window.eh2 = '';
      window.ehttd = '';
    }
  }

  const lastAuthCheck = getLocalStorage(AUTH_CHECK_STORAGE_KEY) || 0;
  const shouldForceCheck = now - lastAuthCheck > AUTH_CHECK_INTERVAL;
  console.log('[HEYGG COMMON] Auth check interval:', AUTH_CHECK_INTERVAL, 'ms');
  console.log('[HEYGG COMMON] Last auth check:', lastAuthCheck, 'ms');
  console.log('[HEYGG COMMON] Current time:', now, 'ms');
  console.log('[HEYGG COMMON] Should force check:', shouldForceCheck);

  // If we have an anonymous ID and we're not due for a forced check, use it
  if (existingAnonymousId && !shouldForceCheck) {
    console.log('[HEYGG COMMON] Using existing anonymous ID:', existingAnonymousId);
    return;
  }

  console.log('[HEYGG COMMON] No valid auth - redirecting to check-auth');
  redirectToCheckAuth(API_BASE_URL, existingAnonymousId);
}

const Google = {
  async handleGoogleCredential(credential: string, redirectUri?: string, apiBaseUrl?: string) {
    console.log('[HEYGG COMMON] Handling Google credential:', credential);
    const API_BASE_URL =
      apiBaseUrl || window.HeyGoodGame?.config?.apiBaseUrl || DEFAULT_API_BASE_URL;
    const existingAnonymousId = getLocalStorage('heygg_anonymous_id');

    try {
      const body: any = { credential };
      if (redirectUri) body.redirect_uri = redirectUri;
      if (existingAnonymousId) body.anonymous_id = existingAnonymousId;

      const response = await fetch(`${API_BASE_URL}/auth/google/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error('Failed to verify Google credential');
      }

      const data = await response.json();

      console.log('[HEYGG COMMON] Google auth response:', data);

      if (data.success && data.token) {
        let returnUrl;

        if (data.is_new_user) {
          console.log('[HEYGG COMMON] New user detected');
          // New user - redirect to welcome page after establishing session
          returnUrl = encodeURIComponent(
            `${API_BASE_URL}/welcome?return_to=${encodeURIComponent(redirectUri || '')}`,
          );
        } else {
          // Existing user - redirect back to intended destination
          returnUrl = encodeURIComponent(data.redirect_url);
        }
        console.log('[HEYGG COMMON] Redirecting to:', returnUrl);

        console.log(
          '[HEYGG COMMON] Establishing SSO session with token:',
          data.token,
        );
        const ssoUrl = `${API_BASE_URL}/auth/sso/establish-session?token=${data.token}&return_to=${returnUrl}`;
        window.location.href = ssoUrl;
      } else if (data.success && data.redirect_url) {
        // No token case (shouldn't happen for game sites, but fallback)
        window.location.href = data.redirect_url;
      } else {
        throw new Error('Invalid response from Google auth endpoint');
      }
    } catch (error) {
      console.error('Error handling Google credential:', error);
      throw error;
    }
  },

  initializeOneTap(
    clientId: string,
    options: { autoSelect?: boolean; cancelOnTapOutside?: boolean; apiBaseUrl?: string } = {},
  ) {
    if (!window.google?.accounts?.id) {
      console.error('Google One Tap library not loaded');
      return;
    }

    const config: GoogleOneTapConfig = {
      client_id: clientId,
      callback: (response: GoogleCredentialResponse) => {
        this.handleGoogleCredential(response.credential, window.location.href, options.apiBaseUrl).catch(
          (error) => {
            console.error('Google One Tap authentication failed:', error);
          },
        );
      },
      auto_select: options.autoSelect ?? false,
      cancel_on_tap_outside: options.cancelOnTapOutside ?? true,
    };

    window.google.accounts.id.initialize(config);
    window.google.accounts.id.prompt((notification) => {
      if (notification.isNotDisplayed()) {
        console.log(
          'Google One Tap not displayed:',
          notification.getNotDisplayedReason(),
        );
      } else if (notification.isSkippedMoment()) {
        console.log('Google One Tap skipped:', notification.getSkippedReason());
      } else if (notification.isDismissedMoment()) {
        console.log('Google One Tap dismissed:', notification.getDismissedReason());
      }
    });
  },
};

export { Google };

export default function auth() {
  return checkAuth();
}
